import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { LayoutGrid, Image, Wrench, MessageSquare, LogOut, Menu, X, Layers, Facebook, Star, Inbox, CalendarCheck, HardDrive, Box, Award, Settings, Users, ShieldCheck, ScrollText, Timer, BarChart2 } from "lucide-react";
import { useAdmin } from "../../context/AdminContext";
import { api } from "../../services/api";

const LOGO = "/logo-kb-icon.png";

const NAV = [
    { to: "/admin/dashboard",    label: "Prehľad",     icon: LayoutGrid    },
    { to: "/admin/stats",        label: "Návštevnosť", icon: BarChart2     },
    { to: "/admin/contacts",     label: "Dopyty",       icon: Inbox         },
    { to: "/admin/referencie",   label: "Referencie",  icon: Image         },
    { to: "/admin/technika",     label: "Technika",    icon: Wrench        },
    { to: "/admin/testimonials", label: "Hodnotenia",  icon: MessageSquare },
    { to: "/admin/sluzby",       label: "Služby",      icon: Layers        },
    { to: "/admin/facebook",     label: "Facebook",    icon: Facebook      },
    { to: "/admin/google",       label: "Google",      icon: Star          },
    { to: "/admin/bookings",     label: "Rezervácie",  icon: CalendarCheck },
    { to: "/admin/backup",          label: "Zálohy",      icon: HardDrive     },
    { to: "/admin/backup-schedule", label: "Auto-zálohy", icon: Timer         },
    { to: "/admin/vizualizacie", label: "3D Vizualizácie", icon: Box        },
    { to: "/admin/brands",       label: "Značky",          icon: Award       },
    { to: "/admin/settings",     label: "Nastavenia",      icon: Settings    },
    { to: "/admin/security",     label: "Zabezpečenie",    icon: ShieldCheck },
    { to: "/admin/users",        label: "Používatelia",    icon: Users,       superadmin: true },
    { to: "/admin/audit",        label: "Audit Log",       icon: ScrollText,  superadmin: true },
];

export default function AdminLayout() {
    const { user, loading, logout } = useAdmin();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [unread, setUnread]           = useState(0);
    const [bookingsNew, setBookingsNew] = useState(0);

    useEffect(() => {
        const refresh = () => api.stats().then(s => {
            setUnread(s.unread || 0);
            setBookingsNew(s.bookings_new || 0);
        }).catch(() => {});
        refresh();
        const id = setInterval(refresh, 60_000);
        return () => clearInterval(id);
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-nova-black flex items-center justify-center">
                <div className="font-mono text-zinc-500 text-sm">Načítanie…</div>
            </div>
        );
    }

    if (!user) return <Navigate to="/admin/login" replace />;

    const handleLogout = () => { logout(); navigate("/admin/login"); };

    const Sidebar = ({ mobile = false }) => (
        <aside className={`${mobile ? "w-full" : "w-60 hidden lg:flex"} flex-col bg-nova-surface/30 border-r border-white/8 min-h-screen`}>
            <div className="flex items-center gap-3 px-5 py-5 border-b border-white/8">
                <img src={LOGO} alt="KB MUSIC" className="w-8 h-8 rounded-full ring-1 ring-nova-cyan/30" />
                <div>
                    <div className="font-heading font-black text-white text-sm tracking-tight leading-none">KB MUSIC</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500">Admin</div>
                </div>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-0.5">
                {NAV.filter(n => !n.superadmin || user?.role === "superadmin").map(({ to, label, icon: Icon }) => (
                    <NavLink
                        key={to}
                        to={to}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                                isActive
                                    ? "bg-nova-cyan/10 text-nova-cyan border-l-2 border-nova-cyan pl-[10px]"
                                    : "text-zinc-400 hover:text-white hover:bg-white/5"
                            }`
                        }
                    >
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="flex-1">{label}</span>
                        {to === "/admin/contacts" && unread > 0 && (
                            <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-nova-cyan text-black text-[10px] font-black flex items-center justify-center">
                                {unread > 99 ? "99+" : unread}
                            </span>
                        )}
                        {to === "/admin/bookings" && bookingsNew > 0 && (
                            <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-black flex items-center justify-center">
                                {bookingsNew > 99 ? "99+" : bookingsNew}
                            </span>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="px-3 py-4 border-t border-white/8">
                <div className="px-3 mb-3">
                    <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">Prihlásený ako</div>
                    <div className="text-white text-sm font-medium">{user.username}</div>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-red-400 hover:bg-red-400/5 transition-colors"
                >
                    <LogOut className="w-4 h-4" /> Odhlásiť sa
                </button>
            </div>
        </aside>
    );

    return (
        <div className="min-h-screen bg-nova-black text-white flex">
            <Sidebar />

            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
                    <div className="absolute left-0 top-0 bottom-0 w-72 z-10">
                        <Sidebar mobile />
                    </div>
                </div>
            )}

            <div className="flex-1 flex flex-col min-w-0">
                {/* Mobile top bar */}
                <div className="lg:hidden flex items-center gap-4 px-4 py-3 border-b border-white/8 bg-nova-surface/20">
                    <button onClick={() => setSidebarOpen(true)} className="text-zinc-400 hover:text-white">
                        <Menu className="w-5 h-5" />
                    </button>
                    <span className="font-heading font-bold text-sm text-white">KB Music Admin</span>
                </div>

                <main className="flex-1 p-6 lg:p-8 overflow-auto">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
